(() => {
  const SECTION_SELECTOR = '[data-wonjo-cart-recommendations]';
  const CARD_SELECTOR = '[data-wonjo-recommendation-product-id]';
  const MAX_SOURCE_PRODUCTS = 3;

  async function fetchCart(root) {
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Cart request failed: ${response.status}`);
    }

    return response.json();
  }

  async function fetchRecommendationSection({
    root,
    sectionId,
    productId,
    requestLimit,
  }) {
    const params = new URLSearchParams({
      section_id: sectionId,
      product_id: String(productId),
      limit: String(requestLimit),
      intent: 'related',
    });

    const response = await fetch(
      `${root}recommendations/products?${params.toString()}`,
      { headers: { Accept: 'text/html' } }
    );

    if (!response.ok) {
      throw new Error(
        `Recommendation request failed for ${productId}: ${response.status}`
      );
    }

    const html = await response.text();
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');

    return parsedDocument.querySelector(SECTION_SELECTOR);
  }

  function combineCards({
    renderedSections,
    cartProductIds,
    displayLimit,
  }) {
    const cardGroups = renderedSections.map((section) =>
      section ? Array.from(section.querySelectorAll(CARD_SELECTOR)) : []
    );

    const selectedCards = [];
    const selectedProductIds = new Set();
    let cardIndex = 0;
    let foundCard = true;

    /*
     * Round-robin selection:
     * one recommendation from source product 1,
     * then source product 2,
     * then source product 3, and repeat.
     */
    while (selectedCards.length < displayLimit && foundCard) {
      foundCard = false;

      cardGroups.forEach((group) => {
        if (selectedCards.length >= displayLimit) return;

        const card = group[cardIndex];
        if (!card) return;

        foundCard = true;

        const productId = String(
          card.dataset.wonjoRecommendationProductId || ''
        );

        if (
          !productId ||
          cartProductIds.has(productId) ||
          selectedProductIds.has(productId)
        ) {
          return;
        }

        selectedProductIds.add(productId);
        selectedCards.push(card);
      });

      cardIndex += 1;
    }

    return selectedCards;
  }

  async function loadWonjoRecommendations(container, force = false) {
    if (!container) return;

    if (!force && container.dataset.wonjoInitialized === 'true') {
      return;
    }

    container.dataset.wonjoInitialized = 'true';

    const sectionId = container.dataset.sectionId;
    const displayLimit = Number(container.dataset.limit || 4);
    const root = window.Shopify?.routes?.root || '/';

    try {
      const cart = await fetchCart(root);

      if (!cart.items?.length || !sectionId) {
        container.hidden = true;
        return;
      }

      const sourceItems = cart.items
        .filter((item) => item.product_id)
        .slice(0, MAX_SOURCE_PRODUCTS);

      const cartProductIds = new Set(
        cart.items.map((item) => String(item.product_id))
      );

      /*
       * Fetch extra results from each product so there are enough left
       * after excluding cart products and duplicate recommendations.
       */
      const requestLimit = Math.min(
        10,
        Math.max(6, displayLimit * 2)
      );

      const renderedSections = await Promise.all(
        sourceItems.map((item) =>
          fetchRecommendationSection({
            root,
            sectionId,
            productId: item.product_id,
            requestLimit,
          }).catch((error) => {
            console.warn(
              `Wonjo recommendations skipped product ${item.product_id}:`,
              error
            );
            return null;
          })
        )
      );

      const usableSections = renderedSections.filter(Boolean);

      if (!usableSections.length) {
        container.hidden = true;
        return;
      }

      const selectedCards = combineCards({
        renderedSections: usableSections,
        cartProductIds,
        displayLimit,
      });

      if (!selectedCards.length) {
        container.hidden = true;
        return;
      }

      /*
       * Use the first Shopify-rendered section as the base so we preserve
       * the Rise heading, grid structure, localization, and card markup.
       */
      const renderedContainer = usableSections[0];
      const renderedGrid = renderedContainer.querySelector(
        '.wonjo-cart-recommendations__grid'
      );

      if (!renderedGrid) {
        container.hidden = true;
        return;
      }

      renderedGrid.innerHTML = '';

      selectedCards.forEach((card) => {
        renderedGrid.appendChild(card.cloneNode(true));
      });

      renderedContainer.hidden = false;
      renderedContainer.dataset.wonjoInitialized = 'true';

      container.replaceWith(renderedContainer);
    } catch (error) {
      console.error('Wonjo recommendations could not load:', error);
      container.hidden = true;
    }
  }

  function initializeWonjoRecommendations(scope = document) {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((container) => {
      loadWonjoRecommendations(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeWonjoRecommendations();
    });
  } else {
    initializeWonjoRecommendations();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeWonjoRecommendations(event.target);
  });
})();

  async function refreshWonjoRecommendations() {
    const container = document.querySelector(SECTION_SELECTOR);
    if (!container) return;

    container.dataset.wonjoInitialized = 'false';
    await loadWonjoRecommendations(container, true);
  }

  if (
    !window.wonjoRecommendationsSubscribed &&
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined'
  ) {
    window.wonjoRecommendationsSubscribed = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
      await new Promise((resolve) => setTimeout(resolve, 150));
      await refreshWonjoRecommendations();
    });
  }